import { type JSX } from 'react';
import { Mail, Ban, CheckCircle, KeyRound } from 'lucide-react';
import { type OrgMemberListItem } from '@bliks-io/contracts';

interface StatusActionHandlers {
  onSendInvitation: (memberId: string) => void;
  onSuspend: (memberId: string) => void;
  onUnsuspend: (memberId: string) => void;
  /** Pages that do not offer a sign-in reset leave this out. */
  onResetSignIn?: (member: OrgMemberListItem) => void;
}

export interface MemberStatusAction {
  label: string;
  icon: JSX.Element;
  onClick: () => void;
  variant?: 'destructive';
}

/**
 * Returns status-specific row actions for a member based on their current status.
 */
export function getMemberStatusActions(
  member: OrgMemberListItem,
  handlers: StatusActionHandlers,
  t: (key: string) => string
): MemberStatusAction[] {
  const actions: MemberStatusAction[] = [];

  if (member.status === 'inactive') {
    actions.push({
      label: t('organization.members.sendInvitationAction'),
      icon: <Mail className="w-4 h-4" />,
      onClick: () => handlers.onSendInvitation(member._id)
    });
  } else if (member.status === 'pending') {
    actions.push({
      label: t('organization.members.resendInvitationAction'),
      icon: <Mail className="w-4 h-4" />,
      onClick: () => handlers.onSendInvitation(member._id)
    });
    actions.push({
      label: t('organization.members.suspendUser'),
      icon: <Ban className="w-4 h-4" />,
      onClick: () => handlers.onSuspend(member._id),
      variant: 'destructive' as const
    });
  } else if (member.status === 'suspended') {
    actions.push({
      label: t('organization.members.unsuspendUser'),
      icon: <CheckCircle className="w-4 h-4" />,
      onClick: () => handlers.onUnsuspend(member._id)
    });
  } else if (member.status === 'active') {
    actions.push({
      label: t('organization.members.suspendUser'),
      icon: <Ban className="w-4 h-4" />,
      onClick: () => handlers.onSuspend(member._id),
      variant: 'destructive' as const
    });
  }

  const { onResetSignIn } = handlers;
  if (member.signInResetAvailable && onResetSignIn) {
    actions.push({
      label: t('organization.members.resetSignInAction'),
      icon: <KeyRound className="w-4 h-4" />,
      onClick: () => onResetSignIn(member),
      variant: 'destructive' as const
    });
  }

  return actions;
}
